import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';
import type {
  LinkPlaidBankAccountInput,
  MutationCompleteVerificationAndCreateProcessorTokenArgs,
  MutationCreateVerificationLinkTokenForAccountArgs,
  MutationPlaidLinkTokenCreateArgs,
  MutationPlaidLinkTokenExchangeArgs,
  MutationUpdatePlaidLinkedBankAccountStatusArgs,
  PlaidLinkToken,
  PlaidLinkedBankAccountSafe,
  PlaidProcessorToken,
  QueryGetPlaidLinkedBankAccountsByPaidolIdArgs,
  VerificationCompleteResponse,
} from 'CDK_API';
import {
  completeVerificationAndCreateProcessorToken,
  createVerificationLinkTokenForAccount,
  linkPlaidBankAccount,
  plaidLinkTokenCreate,
  plaidLinkTokenExchange,
  updatePlaidLinkedBankAccountStatus,
} from 'graphql_custom/plaid/mutations';
import { getPlaidLinkedBankAccountsByPaidolId } from 'graphql_custom/plaid/queries';
import { fetchWithTransportRetry, isNetworkTransportError } from './networkTransport';

/**
 * Plaid bank linking on the CDK API (SEE-1652 / SEE-1228), served by the v2Shared
 * Lambda. Replaces the Amplify plaidApiResolver calls; the documents, variables
 * and result shapes are unchanged, so the thunks built on this return what they
 * always did.
 *
 * Every mutation is single-shot (`retries: 0`): a public token can be exchanged
 * once, so replaying a request that timed out after the server acted would fail a
 * link that actually succeeded. Only the read query retries on a dropped
 * connection.
 */
export class PlaidService {
  private readonly cdkAppSyncEndpoint: string;

  constructor() {
    this.cdkAppSyncEndpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';
  }

  async createLinkToken(args: MutationPlaidLinkTokenCreateArgs): Promise<PlaidLinkToken | null | undefined> {
    const data = await this.mutate<{ plaidLinkTokenCreate?: PlaidLinkToken | null }>(
      plaidLinkTokenCreate,
      args,
      'plaidLinkTokenCreate'
    );
    return data.plaidLinkTokenCreate;
  }

  async exchangeForProcessorToken(
    args: MutationPlaidLinkTokenExchangeArgs
  ): Promise<PlaidProcessorToken | null | undefined> {
    const data = await this.mutate<{ plaidLinkTokenExchange?: PlaidProcessorToken | null }>(
      plaidLinkTokenExchange,
      args,
      'plaidLinkTokenExchange'
    );
    return data.plaidLinkTokenExchange;
  }

  async linkBankAccount(
    input: LinkPlaidBankAccountInput
  ): Promise<PlaidLinkedBankAccountSafe | null | undefined> {
    const data = await this.mutate<{ linkPlaidBankAccount?: PlaidLinkedBankAccountSafe | null }>(
      linkPlaidBankAccount,
      { input },
      'linkPlaidBankAccount'
    );
    return data.linkPlaidBankAccount;
  }

  async listLinkedBankAccounts(
    args: QueryGetPlaidLinkedBankAccountsByPaidolIdArgs
  ): Promise<PlaidLinkedBankAccountSafe[] | undefined> {
    const data = await this.execute<{
      getPlaidLinkedBankAccountsByPaidolId?: { items: PlaidLinkedBankAccountSafe[] } | null;
    }>(getPlaidLinkedBankAccountsByPaidolId, args, 'getPlaidLinkedBankAccountsByPaidolId');
    return data.getPlaidLinkedBankAccountsByPaidolId?.items;
  }

  async createVerificationLinkToken(
    args: MutationCreateVerificationLinkTokenForAccountArgs
  ): Promise<PlaidLinkToken | null | undefined> {
    const data = await this.mutate<{ createVerificationLinkTokenForAccount?: PlaidLinkToken | null }>(
      createVerificationLinkTokenForAccount,
      args,
      'createVerificationLinkTokenForAccount'
    );
    return data.createVerificationLinkTokenForAccount;
  }

  async completeVerification(
    args: MutationCompleteVerificationAndCreateProcessorTokenArgs
  ): Promise<VerificationCompleteResponse | null | undefined> {
    const data = await this.mutate<{
      completeVerificationAndCreateProcessorToken?: VerificationCompleteResponse | null;
    }>(completeVerificationAndCreateProcessorToken, args, 'completeVerificationAndCreateProcessorToken');
    return data.completeVerificationAndCreateProcessorToken;
  }

  async updateLinkedBankAccountStatus(
    args: MutationUpdatePlaidLinkedBankAccountStatusArgs
  ): Promise<PlaidLinkedBankAccountSafe | null | undefined> {
    const data = await this.mutate<{
      updatePlaidLinkedBankAccountStatus?: PlaidLinkedBankAccountSafe | null;
    }>(updatePlaidLinkedBankAccountStatus, args, 'updatePlaidLinkedBankAccountStatus');
    return data.updatePlaidLinkedBankAccountStatus;
  }

  private mutate<T>(query: string, variables: object, operation: string): Promise<T> {
    return this.execute<T>(query, variables, operation, { retries: 0 });
  }

  private async execute<T>(
    query: string,
    variables: object,
    operation: string,
    transport?: { retries: number }
  ): Promise<T> {
    if (!this.cdkAppSyncEndpoint) {
      throw new Error('Bank linking is currently unavailable. Please contact support.');
    }
    try {
      const session = await Auth.currentSession();
      const response = await fetchWithTransportRetry(
        this.cdkAppSyncEndpoint,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', Authorization: session.getIdToken().getJwtToken() },
          body: JSON.stringify({ query, variables }),
        },
        transport
      );
      const json = (await response.json()) as { data?: T; errors?: Array<{ message: string }> };
      reportCdkRefusal(json?.errors, { authMode: 'cognito', operationName: operation });
      if (!response.ok || json.errors?.length) {
        throw new Error(json.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`);
      }
      if (!json.data) {
        throw new Error(`No data returned from ${operation}`);
      }
      return json.data;
    } catch (error) {
      // Tags and the operation name only: variables carry public tokens and grants.
      captureException(error, {
        tags: { area: 'plaid', type: isNetworkTransportError(error) ? 'network-transport' : 'graphql' },
        extra: { operation },
      });
      throw error;
    }
  }
}

export const plaidService = new PlaidService();
